import React, { useEffect, useMemo, useState } from "react";

import { Box, Collapse, Grid, IconButton } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { colors } from "@zapalacorp/tokens";

import SkeletonComponent from "../../../../../components/Generic/Skeleton";
import { useAppState } from "../../../../../context/StateContext";
import Checkbox from "../../../../../components/Form/Checkbox";
import { CompanySecurity } from "../../../../../models/company.security";
import SecurityItem from "./components/SecurityItem";
import { SubTitle } from "./components/SecurityItem/styles";
import useActionsSecurity from "./hooks/actions";
import useStyles, {
  GeneralWrapper,
  OptionsWrapper,
  Title,
  WrapperPlatforms,
  WrapperTitleWithLoading,
} from "./styles";
import MFAComponent from "./MFA";
import { CompanySecurityAttributes, Platforms } from "./util";
import { isMoving } from "Util/validation";
import { Loading } from "./components/SecurityItem/styles";
import { NotificationToast } from "../../../../../components/Generic/Toast/NotificationToast";

const SecurityComponent = () => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const history = useHistory();
  const { currentUser } = useAppState();
  const isMovingCompany = isMoving(currentUser?.company);
  const [companySecurity, setCompanySecurity] = useState<CompanySecurity>();
  const [selectedOption, setSelectedOption] = useState<string[]>([]);
  const [mfaExpanded, setMfaExpanded] = useState<boolean>(false);
  const [prohibitLoginDevices, setProhibitLoginDevices] = useState<string[]>([]);

  const { getByCompanyId, updateSecurity } = useActionsSecurity(setSelectedOption);
  const updateCompanySecurity = (attribute: string, value: boolean) => {
    setCompanySecurity((prev) => ({ ...prev, [attribute]: value }));
  };

  const { refetch, data, isLoading } = getByCompanyId();

  const { mutate: handleUpdateSecurity, isLoading: isUpdate } = updateSecurity(
    companySecurity?.id || 0,
    (companySecurity: CompanySecurity) => {
      refetch();
      setCompanySecurity((prev) => ({ ...prev, ...companySecurity }));
      setProhibitLoginDevices(companySecurity?.prohibitLoginDevices?.split(", ") || []);
    }
  );

  const handleUpdateProhibitLoginDevices = (device: string) => {
    if (prohibitLoginDevices.includes(device) && prohibitLoginDevices?.length === 1) {
      NotificationToast.error(translate("youHaveToSelectAtLeastOnePlatform"));
      return;
    }

    const updatedDevices = prohibitLoginDevices.includes(device)
      ? prohibitLoginDevices.filter((d) => d !== device)
      : [...prohibitLoginDevices, device];

    handleUpdateSecurity({
      prohibitLoginDevices: updatedDevices.join(", "),
      prohibitSimultaneousLogins: companySecurity?.prohibitSimultaneousLogins || false,
    });
    setProhibitLoginDevices(updatedDevices);
    refetch();
  };

  useEffect(() => {
    if (data) {
      setCompanySecurity(data);
      setProhibitLoginDevices(data?.prohibitLoginDevices?.split(", ") || []);
    }
  }, [data]);

  const securityOptions: Array<{
    isEnabled: boolean;
    title: string;
    aditionalInfo: string[];
    value: boolean;
    updateSecurity: (value: boolean) => void;
    attribute: string;
    children?: React.ReactNode;
  }> = useMemo(
    () => [
      {
        isEnabled: true,
        title: translate("limitUnsuccessfulLogonAttempts"),
        aditionalInfo: [
          translate("anThe4thFailedAttemptTheAccountWillBeLockedFor30Seconds"),
          translate("onThe5thFailedAttemptTheAccountWillBeLockedFor60Seconds"),
          translate("onThe6thFailedAttemptTheAccountWillBeLockedFor120Seconds"),
          translate("anyFailedAttemptsAfterThe6thTheAccountWillBeLockedFor120Seconds"),
        ],
        value: !!companySecurity?.limitLogon,
        updateSecurity: (value: boolean) => {
          setSelectedOption((prev) => [...prev, CompanySecurityAttributes.LIMIT_LOGON]);
          updateCompanySecurity(CompanySecurityAttributes.LIMIT_LOGON, value);
          handleUpdateSecurity({
            [CompanySecurityAttributes.LIMIT_LOGON]: value,
          } as CompanySecurity);
        },
        attribute: CompanySecurityAttributes.LIMIT_LOGON,
      },
      {
        isEnabled: true,
        title: translate("autoTerminateSessionsAfter8HoursFromLogin"),
        aditionalInfo: [
          translate(
            "thePlatformCalculates8HoursFromTheMostRecentLoginAndTerminatesTheSessionRequiringTheUserToLogInAgain"
          ),
          translate("aWarningAppears15MinutesBeforeTheSessionTeminates"),
        ],
        value: !!companySecurity?.autoTerminateSession,
        updateSecurity: (value: boolean) => {
          setSelectedOption((prev) => [
            ...prev,
            CompanySecurityAttributes.AUTO_TERMINATION_SESSION,
          ]);
          updateCompanySecurity(CompanySecurityAttributes.AUTO_TERMINATION_SESSION, value);
          handleUpdateSecurity({
            [CompanySecurityAttributes.AUTO_TERMINATION_SESSION]: value,
          } as CompanySecurity);
        },
        attribute: CompanySecurityAttributes.AUTO_TERMINATION_SESSION,
      },
      {
        isEnabled: !!isMoving(currentUser?.company),
        title: translate("prohibitSimultaneousLogin"),
        aditionalInfo: [translate("prohibitSimultaneousLoginDescription")],
        value: !!companySecurity?.prohibitSimultaneousLogins,
        updateSecurity: (value: boolean) => {
          setSelectedOption((prev) => [
            ...prev,
            CompanySecurityAttributes.PROHIBIT_SIMULTANEOUS_LOGINS,
          ]);
          updateCompanySecurity(CompanySecurityAttributes.PROHIBIT_SIMULTANEOUS_LOGINS, value);

          if (value === false) {
            handleUpdateSecurity({
              [CompanySecurityAttributes.PROHIBIT_SIMULTANEOUS_LOGINS]: value,
              [CompanySecurityAttributes.PROHIBIT_LOGIN_DEVICES]: "",
            } as CompanySecurity);
            setProhibitLoginDevices([]);
          } else {
            handleUpdateSecurity({
              [CompanySecurityAttributes.PROHIBIT_SIMULTANEOUS_LOGINS]: value,
              [CompanySecurityAttributes.PROHIBIT_LOGIN_DEVICES]: "web",
            } as CompanySecurity);
            setProhibitLoginDevices((prev) => [...(prev || []), "web"]);
          }
        },
        attribute: CompanySecurityAttributes.PROHIBIT_SIMULTANEOUS_LOGINS,
        children: !!companySecurity?.prohibitSimultaneousLogins ? (
          <WrapperPlatforms>
            <WrapperTitleWithLoading>
              <span>{translate("applyToPlatforms")}:</span>
              {(isLoading || isUpdate) && <Loading size={16} />}
            </WrapperTitleWithLoading>
            <div>
              <Checkbox
                checked={
                  prohibitLoginDevices?.includes(Platforms.WEB) ||
                  companySecurity.prohibitLoginDevices?.includes(Platforms.WEB)
                }
                label="Web"
                onChange={(e) => handleUpdateProhibitLoginDevices(Platforms.WEB)}
              />
              <Checkbox
                checked={
                  prohibitLoginDevices?.includes(Platforms.OPS) ||
                  companySecurity.prohibitLoginDevices?.includes(Platforms.OPS)
                }
                label="OPS app"
                onChange={(e) => handleUpdateProhibitLoginDevices(Platforms.OPS)}
              />
              <Checkbox
                checked={
                  prohibitLoginDevices?.includes(Platforms.SALES) ||
                  companySecurity.prohibitLoginDevices?.includes(Platforms.SALES)
                }
                label="Sales app"
                onChange={(e) => handleUpdateProhibitLoginDevices(Platforms.SALES)}
              />
            </div>
          </WrapperPlatforms>
        ) : null,
      },
    ],
    [companySecurity, isLoading, isUpdate, prohibitLoginDevices]
  );

  const passwordOptions = useMemo(
    () => [
      {
        title: translate("minimumPasswordComplexityPolicy"),
        aditionalInfo: [
          translate(
            "passwordsMustFulfilMinimumRequirementsForAComplexPasswordWhenCreatingOrResettingAPassword"
          ),
        ],
        value: !!companySecurity?.passwordComplexity,
        updateSecurity: (value: boolean) => {
          setSelectedOption((prev) => [...prev, CompanySecurityAttributes.PASSWORD_COMPLEXITY]);
          updateCompanySecurity(CompanySecurityAttributes.PASSWORD_COMPLEXITY, value);
          handleUpdateSecurity({
            [CompanySecurityAttributes.PASSWORD_COMPLEXITY]: value,
          } as CompanySecurity);
        },
        attribute: CompanySecurityAttributes.PASSWORD_COMPLEXITY,
      },
      {
        title: translate("prohibitPasswordReuse"),
        aditionalInfo: [
          translate("prohibitUsersFromReusingAPreviousPasswordWhenCreatingOrResettingAPassword"),
        ],
        value: !!companySecurity?.passwordReuse,
        updateSecurity: (value: boolean) => {
          setSelectedOption((prev) => [...prev, CompanySecurityAttributes.PASSWORD_REUSE]);
          updateCompanySecurity(CompanySecurityAttributes.PASSWORD_REUSE, value);
          handleUpdateSecurity({
            [CompanySecurityAttributes.PASSWORD_REUSE]: value,
          } as CompanySecurity);
        },
        attribute: CompanySecurityAttributes.PASSWORD_REUSE,
      },
    ],

    [companySecurity]
  );

  return (
    <GeneralWrapper>
      <Grid item container direction="column" className={classes.contentSection}>
        <Grid
          item
          container
          direction="column"
          className={`${classes.wrapperContentPlatforms} ${classes.sectionSpacing}`}
        >
          {/* </Grid><Grid item container direction="column" className={classes.contentSection} spacing={3}> */}
          {/* <Grid item>
          <Title>{translate("security")}</Title>
        </Grid> */}
          <Grid item container direction="column">
            <OptionsWrapper>
              {securityOptions
                ?.filter((option) => option.isEnabled)
                .map((option, index) => (
                  <SkeletonComponent key={index} isLoading={isLoading}>
                    <SecurityItem
                      {...option}
                      key={index}
                      isLoading={isUpdate}
                      selectedOption={selectedOption}
                    ></SecurityItem>
                  </SkeletonComponent>
                ))}
            </OptionsWrapper>
          </Grid>
        </Grid>
        <Grid item className={classes.sectionSpacing}>
          <Title>{translate("password")}</Title>
        </Grid>
        <Grid
          item
          container
          direction="column"
          className={`${classes.wrapperContentPlatforms} ${classes.sectionSpacing}`}
        >
          <OptionsWrapper>
            {passwordOptions?.map((option, index) => (
              <SkeletonComponent key={index} isLoading={isLoading}>
                <SecurityItem
                  {...option}
                  key={index}
                  isLoading={isUpdate}
                  selectedOption={selectedOption}
                ></SecurityItem>
              </SkeletonComponent>
            ))}
          </OptionsWrapper>
        </Grid>
        {isMovingCompany && (
          <>
            <Grid item className={classes.sectionSpacing}>
              <Title>{translate("multiFactorAuthentication")}</Title>
            </Grid>
            <Grid item xs={12} className={classes.wrapperContentPlatforms}>
              <OptionsWrapper>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "12px 24px",
                    borderBottom: `1px solid ${colors.gray200}`,
                    cursor: "pointer",
                  }}
                  onClick={() => setMfaExpanded(!mfaExpanded)}
                >
                  <SubTitle>{translate("configureMfaSettingsPerGroup")}</SubTitle>
                  <IconButton
                    size="small"
                    aria-label={mfaExpanded ? "collapse" : "expand"}
                    onClick={(e) => {
                      e.stopPropagation();
                      setMfaExpanded(!mfaExpanded);
                    }}
                  >
                    {mfaExpanded ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>
                <Collapse in={mfaExpanded} unmountOnExit>
                  <MFAComponent inline onBack={() => setMfaExpanded(false)} />
                </Collapse>
              </OptionsWrapper>
            </Grid>
          </>
        )}
      </Grid>
    </GeneralWrapper>
  );
};

export default SecurityComponent;
